import React, { useState, useCallback } from 'react'
import jwt_decode from 'jwt-decode'
import { useDispatch, useSelector } from 'react-redux'
import {
  ActionsSectionInline,
  ActionsWrapper,
  ActionButton
} from './ActionsSection.styles'
import { useTranslation } from 'react-i18next'
import ReleasePlaylistDropdown from 'components/Dropdowns/PlaylistDropdown'
import { FlagHandlerProvider, ArchiveHandlerProvider } from 'components'
import ReleaseActions from 'reducers/Releases'
import ContactModal from '../Modals/ContactModal/ContactModal'
import ModalActions from 'reducers/Modal'
import { ReleasesService } from 'services'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

import {
  ArchiveUnfilled,
  FlagUnfilled,
  Mail,
  Playlistadd,
  ShareArrow
} from '@dsny/dsny-component-library/icons'
import ReleaseActionTile from './ReleaseActionTile'

const ActionsSectionNew = ({ release, setRelease }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const { currentReleaseInformation } = useSelector((state) => state.releases)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  let isAbleToShareRelease = false
  try {
    const token = localStorage.getItem('accessToken')
    const currentUser = jwt_decode(token)
    isAbleToShareRelease =
      currentUser?.verified && currentReleaseInformation?.allow_sharing
  } catch (e) {
    isAbleToShareRelease = false
  }

  const setFlagOnRelease = (flag) => {
    const updatedRelease = {
      ...release,
      flagged: flag,
      songs: release.songs?.map((song) => ({
        ...song,
        flagged: flag
      }))
    }

    setRelease(updatedRelease)
    dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
    dispatch(ReleaseActions.updateRelease(updatedRelease))

    trackEvent('Release Flag', {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      action: flag ? 'flag' : 'unflag',
      source: 'release_information',
      type: 'release_flag'
    })
  }

  const setArchiveOnRelease = (archived) => {
    const shouldUnflagOnArchive = archived && release?.flagged
    const updatedRelease = {
      ...release,
      archived,
      flagged: shouldUnflagOnArchive ? false : release.flagged,
      songs: shouldUnflagOnArchive
        ? release.songs?.map((song) => ({
            ...song,
            flagged: false
          }))
        : release.songs
    }

    setRelease(updatedRelease)
    dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
    dispatch(ReleaseActions.updateRelease(updatedRelease))

    trackEvent('Release Archive', {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      action: archived ? 'archive' : 'unarchive',
      unflagged_on_archive: normalizeValue(archived && release?.flagged),
      source: 'release_information',
      type: 'release_archive'
    })
  }

  const openContactModal = () => {
    setIsContactModalOpen(true)

    try {
      ReleasesService.trackCommentingInit(currentReleaseInformation?.releaseid)
    } catch (error) {
      console.log('Track error', error)
    }
    trackEvent('Release Contact', {
      release_id: normalizeValue(currentReleaseInformation?.releaseid),
      artist: normalizeValue(currentReleaseInformation?.artist),
      title: normalizeValue(currentReleaseInformation?.title),
      source: 'release_information_actions_section',
      type: 'contact'
    })
  }

  const openShareReleaseModal = useCallback(() => {
    dispatch(ModalActions.toggleShareReleaseModal())
  }, [])

  if (!currentReleaseInformation) {
    return null
  }

  return (
    <>
      <ActionsSectionInline $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        <ActionsWrapper
          $responsiveBreakpointsV2={responsiveBreakpointsV2}
          style={{ padding: 0 }}
        >
          <ActionButton $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            {isAbleToShareRelease && (
              <ReleaseActionTile
                icon={<ShareArrow width={24} height={24} />}
                iconOffsetX={1}
                label={t('general.share')}
                onClick={openShareReleaseModal}
              />
            )}
            {currentReleaseInformation?.commenting_enabled && (
              <ReleaseActionTile
                onClick={openContactModal}
                label={t('release-info.contact')}
                icon={<Mail width={24} height={24} />}
              />
            )}
            <ReleasePlaylistDropdown
              renderTrigger={() => (
                <ReleaseActionTile
                  icon={<Playlistadd width={24} height={24} />}
                  label={t('general.addto')}
                />
              )}
              triggerAsChild
            />

            <FlagHandlerProvider
              release={release}
              callback={setFlagOnRelease}
              render={({ handleFlag }) => {
                return (
                  <ReleaseActionTile
                    onClick={handleFlag}
                    label={
                      release?.flagged ? t('general.unflag') : t('general.flag')
                    }
                    icon={<FlagUnfilled width={24} height={24} />}
                  />
                )
              }}
            />
            <ArchiveHandlerProvider
              release={release}
              callback={setArchiveOnRelease}
              render={({ handleArchive }) => {
                return (
                  <ReleaseActionTile
                    onClick={handleArchive}
                    label={
                      release?.archived
                        ? t('general.unarchive')
                        : t('general.archive')
                    }
                    icon={<ArchiveUnfilled width={24} height={24} />}
                  />
                )
              }}
            />
          </ActionButton>
        </ActionsWrapper>
      </ActionsSectionInline>
      <ContactModal
        isContactModalOpen={isContactModalOpen}
        setIsContactModalOpen={setIsContactModalOpen}
      />
    </>
  )
}

export default ActionsSectionNew
